import { tableAbsente } from '@imp/shared/erreurs-supabase';
import {
  EMAIL_SETTINGS_DEFAULT,
  type EmailSettings,
  remplirModele,
  type StatutEmail,
} from '@imp/shared/relances-email';

import { supabase } from '@/lib/supabase';
import { ORG_ID } from '@/modules/clients/api';

// ── Types ────────────────────────────────────────────────────────────────────
export interface SmsConversation {
  id: string;
  contact_id: string | null;
  telephone: string;
  statut: 'actif' | 'opt_out';
  dernier_message_at: string | null;
  dernier_message_extrait: string | null;
  dernier_message_sens: 'entrant' | 'sortant' | null;
  non_lus: number;
  created_at: string;
}

export interface SmsMessage {
  id: string;
  conversation_id: string;
  contact_id: string | null;
  direction: 'entrant' | 'sortant';
  telephone: string;
  corps: string;
  statut: 'en_file' | 'envoye' | 'delivre' | 'echec' | 'recu';
  erreur: string | null;
  relance_type: string | null;
  est_automatique: boolean;
  lu: boolean;
  created_at: string;
  sent_at: string | null;
}

export interface DeclencheurCfg {
  actif: boolean;
  paliers?: number[];
}
export interface SmsSettings {
  actif: boolean;
  heureDebut: number;
  heureFin: number;
  joursOuvres: number[];
  lienAvis: string;
  declencheurs: {
    devis: DeclencheurCfg;
    facture: DeclencheurCfg;
    rdv: DeclencheurCfg;
    avis: DeclencheurCfg;
  };
  templates: { devis: string; facture: string; rdv: string; avis: string };
}

export const SMS_SETTINGS_DEFAULT: SmsSettings = {
  actif: false,
  heureDebut: 9,
  heureFin: 19,
  joursOuvres: [1, 2, 3, 4, 5, 6],
  lienAvis: 'https://search.google.com/local/writereview?placeid=ChIJUUwRO-u1yxIR0judchQIvqw',
  declencheurs: {
    devis: { actif: true, paliers: [7, 14] },
    facture: { actif: true, paliers: [15, 30, 45] },
    rdv: { actif: true },
    avis: { actif: true },
  },
  // Messages types d'une CONVERSATION : l'opérateur (Octopush) reçoit les
  // réponses et honore le STOP. Avec un opérateur sortant seul (Brevo), le
  // moteur les remplace par leur version sans réponse (décision du 26/10/2026).
  templates: {
    devis:
      "Bonjour {prenom}, IMP TOITURE. Avez-vous pu étudier notre devis {numero} ({montant} € TTC) ? Nous restons à votre disposition. Répondez à ce SMS ou au 04 92 77 14 78. STOP pour vous désinscrire.",
    facture:
      "Bonjour {prenom}, IMP TOITURE. Votre facture {numero} de {montant} € est arrivée à échéance. Merci de régulariser. Une question ? Répondez à ce SMS. STOP pour vous désinscrire.",
    rdv:
      "Bonjour {prenom}, IMP TOITURE. Rappel : intervention prévue demain ({date}) à {ville}. Merci de nous prévenir en cas d'empêchement. Répondez à ce SMS.",
    avis:
      "Bonjour {prenom}, merci d'avoir fait confiance à IMP TOITURE ! Si vous êtes satisfait, votre avis Google nous aiderait beaucoup : {lien} — STOP pour vous désinscrire.",
  },
};

// ── Conversations ────────────────────────────────────────────────────────────
export async function listConversations(): Promise<SmsConversation[]> {
  const { data, error } = await supabase
    .from('sms_conversations')
    .select(
      'id, contact_id, telephone, statut, dernier_message_at, dernier_message_extrait, dernier_message_sens, non_lus, created_at',
    )
    .is('deleted_at', null)
    .order('dernier_message_at', { ascending: false, nullsFirst: false });
  if (error) throw new Error(error.message);
  return data;
}

export async function getConversationByContact(contactId: string): Promise<SmsConversation | null> {
  const { data, error } = await supabase
    .from('sms_conversations')
    .select(
      'id, contact_id, telephone, statut, dernier_message_at, dernier_message_extrait, dernier_message_sens, non_lus, created_at',
    )
    .eq('contact_id', contactId)
    .is('deleted_at', null)
    .order('dernier_message_at', { ascending: false, nullsFirst: false })
    .limit(1)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (data) ?? null;
}

export async function listMessages(conversationId: string): Promise<SmsMessage[]> {
  const { data, error } = await supabase
    .from('sms_messages')
    .select(
      'id, conversation_id, contact_id, direction, telephone, corps, statut, erreur, relance_type, est_automatique, lu, created_at, sent_at',
    )
    .eq('conversation_id', conversationId)
    .is('deleted_at', null)
    .order('created_at', { ascending: true });
  if (error) throw new Error(error.message);
  return data;
}

// Envoi via la fonction Edge sms-send (la clé opérateur reste côté serveur).
export async function sendSms(input: {
  telephone: string;
  corps: string;
  contact_id?: string | null;
}): Promise<{ ok: boolean; skipped?: string; error?: string; statut?: string }> {
  const { data, error } = await supabase.functions.invoke<{
    ok: boolean;
    skipped?: string;
    error?: string;
    statut?: string;
  }>('sms-send', { body: input });
  if (error) {
    // Erreur transport ; tente de lire le corps de réponse s'il existe.
    const ctx = (error as { context?: { body?: unknown } }).context;
    const body = typeof ctx?.body === 'string' ? ctx.body : undefined;
    return { ok: false, error: body ?? error.message };
  }
  return data ?? { ok: false, error: 'Réponse vide du serveur.' };
}

export async function markConversationRead(conversationId: string): Promise<void> {
  await supabase
    .from('sms_messages')
    .update({ lu: true })
    .eq('conversation_id', conversationId)
    .eq('direction', 'entrant')
    .eq('lu', false);
  await supabase.from('sms_conversations').update({ non_lus: 0 }).eq('id', conversationId);
}

// ── Réglages relances (organizations.parametres.sms) ─────────────────────────
export async function getSmsSettings(): Promise<SmsSettings> {
  const { data, error } = await supabase
    .from('organizations')
    .select('parametres')
    .eq('id', ORG_ID)
    .single();
  if (error) throw new Error(error.message);
  const sms = (data.parametres as Record<string, unknown> | null)?.sms as Partial<SmsSettings> | undefined;
  return {
    ...SMS_SETTINGS_DEFAULT,
    ...(sms ?? {}),
    declencheurs: { ...SMS_SETTINGS_DEFAULT.declencheurs, ...(sms?.declencheurs ?? {}) },
    templates: { ...SMS_SETTINGS_DEFAULT.templates, ...(sms?.templates ?? {}) },
  };
}

export async function saveSmsSettings(settings: SmsSettings): Promise<void> {
  const { data, error } = await supabase
    .from('organizations')
    .select('parametres')
    .eq('id', ORG_ID)
    .single();
  if (error) throw new Error(error.message);
  const parametres = { ...((data.parametres as Record<string, unknown> | null) ?? {}), sms: settings };
  const { error: upErr } = await supabase.from('organizations').update({ parametres }).eq('id', ORG_ID);
  if (upErr) throw new Error(upErr.message);
}

// ── Relances par e-mail (Brevo) — organizations.parametres.email ─────────────

export async function getEmailSettings(): Promise<EmailSettings> {
  const { data, error } = await supabase
    .from('organizations')
    .select('parametres')
    .eq('id', ORG_ID)
    .single()
    .overrideTypes<{ parametres: unknown }, { merge: false }>();
  if (error) throw new Error(error.message);
  const email = (data.parametres as Record<string, unknown> | null)?.email as Partial<EmailSettings> | undefined;
  return { ...EMAIL_SETTINGS_DEFAULT, ...(email ?? {}) };
}

/** Relit, fusionne, réécrit : les réglages SMS et les autres restent intacts. */
export async function saveEmailSettings(settings: EmailSettings): Promise<void> {
  const { data, error } = await supabase
    .from('organizations')
    .select('parametres')
    .eq('id', ORG_ID)
    .single()
    .overrideTypes<{ parametres: unknown }, { merge: false }>();
  if (error) throw new Error(error.message);
  const parametres = { ...((data.parametres as Record<string, unknown> | null) ?? {}), email: settings };
  const { error: upErr } = await supabase.from('organizations').update({ parametres }).eq('id', ORG_ID);
  if (upErr) throw new Error(upErr.message);
}

export interface EmailEnvoi {
  id: string;
  created_at: string;
  destinataire: string;
  objet: string;
  relance_type: string | null;
  entite_type: string | null;
  entite_id: string | null;
  palier: string | null;
  est_automatique: boolean;
  statut: StatutEmail;
  erreur: string | null;
}

/** Les derniers e-mails partis par Brevo, et ce qu'il en est advenu. */
export async function listEmailEnvois(limite = 50): Promise<EmailEnvoi[]> {
  const { data, error } = await supabase
    .from('email_envois')
    .select('id,created_at,destinataire,objet,relance_type,entite_type,entite_id,palier,est_automatique,statut,erreur')
    .eq('organization_id', ORG_ID)
    .order('created_at', { ascending: false })
    .limit(limite)
    .overrideTypes<EmailEnvoi[], { merge: false }>();
  if (error) {
    if (tableAbsente(error.message, 'email_envois'))
      throw new Error('Mise à jour de la base en attente : le journal des e-mails a besoin de la migration 30.');
    throw new Error(error.message);
  }
  return data;
}

/** Un e-mail de test, rempli comme une vraie relance, vers l'adresse donnée. */
export async function envoyerEmailTest(to: string, settings: EmailSettings): Promise<void> {
  const vars = { prenom: 'Corinne', numero: 'DEV-2026-0042', montant: '22 724,60', date: '12/10/2026' };
  const reponse = await supabase.functions.invoke('email-send', {
    body: {
      to,
      subject: `[TEST] ${remplirModele(settings.objet, vars)}`,
      text: remplirModele(settings.message, vars),
      relance_type: 'test',
    },
  });
  const data = reponse.data as { ok?: boolean; error?: string } | null;
  const error = reponse.error as (Error & { context?: Response }) | null;
  if (error) {
    // L'erreur de la fonction est dans le corps de la réponse.
    const corps = error.context
      ? ((await error.context.json().catch(() => null)) as { error?: string } | null)
      : null;
    throw new Error(corps?.error ?? error.message);
  }
  if (!data?.ok) throw new Error(data?.error ?? 'Envoi refusé.');
}
